import BaseModel from 'volta/models/base-model';
import { attr, belongsTo } from '@ember-data/model';
import { _normalize, collectionCommand, resourceCommand } from 'volta/utils/api';

import type AduProfile from 'volta/models/adu-profile';
import type BufferProfile from 'volta/models/buffer-profile';
import type DafProfileModel from 'volta/models/daf-profile';

import type {
  IPlanningSettingData,
  IUpdateManyPlanningSettings,
  IUpdatePlanningSettingsCmd
} from './types/planning-settings';
import { Type } from '@warp-drive/core-types/symbols';

export const DAF_FIELDS = [
  'dafProfileId',
  'dafWeekly',
  'dafMonthly',
  'dafRedBaseWeekly',
  'dafRedBaseMonthly',
  'dafRedSafetyWeekly',
  'dafRedSafetyMonthly',
  'dafYellowWeekly',
  'dafYellowMonthly',
  'dafGreenWeekly',
  'dafGreenMonthly',
  'dafLtWeekly',
  'dafLtMonthly',
  'dafUnit',
  'dafRedBaseUnit',
  'dafRedSafetyUnit',
  'dafGreenUnit',
  'dafYellowUnit',
  'dafLtUnit'
];

export type TDafField =
  | 'dafProfileId'
  | 'dafWeekly'
  | 'dafMonthly'
  | 'dafRedBaseWeekly'
  | 'dafRedBaseMonthly'
  | 'dafRedSafetyWeekly'
  | 'dafRedSafetyMonthly'
  | 'dafYellowWeekly'
  | 'dafYellowMonthly'
  | 'dafGreenWeekly'
  | 'dafGreenMonthly'
  | 'dafLtWeekly'
  | 'dafLtMonthly'
  | 'dafUnit'
  | 'dafRedBaseUnit'
  | 'dafRedSafetyUnit'
  | 'dafGreenUnit'
  | 'dafYellowUnit'
  | 'dafLtUnit';

export const ResourceName = {
  singular: 'planningSetting',
  plural: 'planningSettings'
};

export const Events = {
  ManyPlanningSettingsUpdated: 'ManyPlanningSettingsUpdated',
  PlanningSettingsUpdated: 'PlanningSettingsUpdated'
};

export const Commands = {
  FetchPlanningSettings: 'FetchPlanningSettings',
  UpdatePlanningSettings: 'UpdatePlanningSettings',
  UpdateManyPlanningSettings: 'UpdateManyPlanningSettings'
};

export interface IPlanningSettings {
  skuId: string;
  settings: IPlanningSettingData;
  bufferProfile?: BufferProfile;
  aduProfile?: AduProfile;
  dafProfile?: DafProfileModel;
}

export default class PlanningSetting extends BaseModel implements IPlanningSettings {
  // Static class methods
  // ~~~~~

  static updateManyPlanningSettings = collectionCommand<
    IUpdateManyPlanningSettings,
    PlanningSetting[],
    typeof PlanningSetting
  >(Commands.UpdateManyPlanningSettings);

  // Properties
  // ~~~~~

  @attr('string')
  declare skuId: string;

  @attr()
  settings!: IPlanningSettingData;

  @belongsTo('buffer-profile', { async: false, inverse: null })
  bufferProfile?: BufferProfile;

  @belongsTo('adu-profile', { async: false, inverse: null })
  aduProfile?: AduProfile;

  @belongsTo('daf-profile', { async: false, inverse: null })
  dafProfile?: DafProfileModel;

  // Getters
  // ~~~~~

  get isBuffered() {
    return this.settings.methodology === 'BUFFER';
  }

  get isMrp() {
    return this.settings.methodology === 'MRP';
  }

  get displayMethodology() {
    return this.settings.methodology ?? 'NONE';
  }

  get actualLeadTimeFactor() {
    return this.settings.leadTimeFactor ?? this.bufferProfile?.leadTimeFactor;
  }

  get actualVarFactor() {
    return this.settings.varFactor ?? this.bufferProfile?.varFactor;
  }

  get actualAduType() {
    return this.aduProfile?.aduType ?? this.settings.aduType;
  }

  get actualAduHorizon() {
    return this.aduProfile?.aduHorizon ?? this.settings.aduHorizon;
  }

  get actualAduHorizonOffset() {
    return this.aduProfile?.aduHorizonOffset ?? this.settings.aduHorizonOffset;
  }

  get actualAduOverride() {
    return this.aduProfile?.aduOverride ?? this.settings.aduOverride;
  }

  get actualDiscontinuedDemand() {
    return this.aduProfile?.discontinuedDemand ?? this.settings.discontinuedDemand;
  }

  // Instance Commands
  // ~~~~~

  updatePlanningSettings = resourceCommand<IUpdatePlanningSettingsCmd, PlanningSetting>(
    Commands.UpdatePlanningSettings
  );

  [Type] = 'planning-setting' as const;
}
